import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { userProfileService } from '../../services/supabaseService';
import ThemeToggle from '../../components/ui/ThemeToggle';
import ProfileHeader from './components/ProfileHeader';
import AccountSettings from './components/AccountSettings';
import NotificationPreferences from './components/NotificationPreferences';
import PaymentMethods from './components/PaymentMethods';
import RideHistorySummary from './components/RideHistorySummary';
import ReferralSection from './components/ReferralSection';
import DangerZone from './components/DangerZone';
import AddressManagement from './components/AddressManagement';

const CustomerProfileScreen = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const profileData = await userProfileService?.getProfile(user?.id);
          setProfile(profileData);

          // Load notification prefs from metadata
          const meta = user?.user_metadata;
          if (meta?.notifPrefs) setNotifPrefs(meta.notifPrefs);

          // Load payment methods from profile metadata
          if (meta?.paymentMethods) setPaymentMethods(meta.paymentMethods);
        } else {
          // Guest view with empty profile
          setProfile({ fullName: 'Guest User', email: '', phone: '' });
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setProfile({ fullName: 'Guest User', email: '', phone: '' });
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleUpdateProfile = async (updates) => {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    const fieldMap = {
      fullName: 'fullName',
      phone: 'phone',
      email: 'email',
    };
    await userProfileService?.updateProfile(user?.id, updates);
    setProfile(prev => ({ ...prev, ...updates }));
  };

  const handleSaveNotifPrefs = async (prefs) => {
    const { error } = await supabase?.auth?.updateUser({ data: { notifPrefs: prefs } });
    if (error) throw error;
    setNotifPrefs(prefs);
  };

  const handleAddPayment = async (method) => {
    const newMethods = [...paymentMethods, { ...method, id: `pm-${Date.now()}`, isDefault: paymentMethods?.length === 0 }];
    setPaymentMethods(newMethods);
    await supabase?.auth?.updateUser({ data: { paymentMethods: newMethods } });
  };

  const handleRemovePayment = async (id) => {
    const newMethods = paymentMethods?.filter(m => m?.id !== id);
    setPaymentMethods(newMethods);
    await supabase?.auth?.updateUser({ data: { paymentMethods: newMethods } });
  };

  const handleSetDefaultPayment = async (id) => {
    const newMethods = paymentMethods?.map(m => ({ ...m, isDefault: m?.id === id }));
    setPaymentMethods(newMethods);
    await supabase?.auth?.updateUser({ data: { paymentMethods: newMethods } });
  };

  const handleSignOut = async () => {
    await supabase?.auth?.signOut();
    navigate('/customer-login-screen');
  };

  const handleAccountDeleted = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1a365d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      {/* Top Nav */}
      <div className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/customer-booking-screen')}
            className="flex items-center gap-2 text-[#1a365d] dark:text-blue-400 font-medium hover:text-[#2d5a87] dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Booking</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#1a365d] dark:text-white">On</span>
            <span className="text-sm font-bold text-[#ffd700] bg-[#1a365d] dark:bg-slate-700 px-1.5 py-0.5 rounded">TheGo</span>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="heading-1 text-[#1a365d] dark:text-white mb-1">My Profile</h1>
            <p className="body-text text-slate-500 dark:text-slate-400">Manage your account settings and preferences</p>
          </div>
          <div className="flex-shrink-0 pt-1">
            <ThemeToggle variant="pill" />
          </div>
        </div>

        <ProfileHeader profile={profile} onEditPhoto={() => {}} />

        <AccountSettings profile={profile} onUpdate={handleUpdateProfile} />

        <NotificationPreferences preferences={notifPrefs} onSave={handleSaveNotifPrefs} />

        <PaymentMethods
          methods={paymentMethods}
          onAdd={handleAddPayment}
          onRemove={handleRemovePayment}
          onSetDefault={handleSetDefaultPayment}
        />

        <AddressManagement onAutofill={() => {}} />

        <RideHistorySummary stats={profile?.activityStats} />

        <ReferralSection
          referralCode={profile?.referralCode}
          referralCount={profile?.referralCount}
          referralEarnings={profile?.referralEarnings}
        />

        <DangerZone onDeleted={handleAccountDeleted} />
      </div>
    </div>
  );
};

export default CustomerProfileScreen;
